import { Button, Tooltip } from 'antd';
import { type ButtonProps } from 'antd/lib/button';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { ModalDialog, type ModalDialogProps } from '../modal/ModalDialog.tsx';

/**
 * Possibilità di passare le props della modal con callback; in questo modo
 * ad esempio si può avere un `cancelButtonProps` che chiude la modal.
 */
export interface ModalButtonCallbackProps {
  open: boolean;
  close: () => void;
}

interface ModalButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  buttonChildren?: ReactNode;
  children: ReactNode;
  tooltip?: string;
  modalProps?:
    | Omit<ModalDialogProps, 'children'>
    | ((props: ModalButtonCallbackProps) => Omit<ModalDialogProps, 'children'>);
  onOk?: () => void;
  onCancel?: () => void;
  onOpen?: () => void;
}

export function ModalButton(props: ModalButtonProps) {
  const {
    children,
    tooltip = props.title,
    onOk,
    onCancel,
    onOpen,
    modalProps: rawModalProps,
    buttonChildren,
    ...buttonProps
  } = props;
  const [open, setOpen] = useState(false);

  const handleOk = useCallback(() => {
    onOk?.();
    setOpen(false);
  }, [onOk]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    setOpen(false);
  }, [onCancel]);

  const handleOpen = useCallback(() => {
    onOpen?.();
    setOpen(true);
  }, [onOpen]);

  const modalProps = useMemo(() => {
    if (typeof rawModalProps === 'function') {
      return rawModalProps({ open, close: handleCancel });
    }
    return rawModalProps ?? {};
  }, [rawModalProps, open, handleCancel]);

  return (
    <>
      <Tooltip title={tooltip}>
        <Button {...buttonProps} onClick={handleOpen}>
          {buttonChildren}
        </Button>
      </Tooltip>

      <ModalDialog
        open={open}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose
        title={modalProps.title ?? props.title}
        {...modalProps}
      >
        {children}
      </ModalDialog>
    </>
  );
}

export default ModalButton;
